import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  aboutFaqs,
  pageDescriptions,
  pageTitles,
  projects,
  site,
} from '../data/site';

const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
  const selector = attr === 'property' ? `meta[property="${key}"]` : `meta[name="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
};

const personJson = () => ({
  '@type': 'Person',
  '@id': `${site.url}/#person`,
  name: site.name,
  alternateName: [site.asciiName, 'Mustafa Gürses yazılımcı', 'Mustafa Gurses developer'],
  jobTitle: 'Yazılımcı, Full Stack Developer',
  description: pageDescriptions['/'],
  url: site.url,
  email: `mailto:${site.email}`,
  image: `${site.url}/brand/logo.png`,
  nationality: 'TR',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Gaziantep',
    addressCountry: 'TR',
  },
  worksFor: {
    '@type': 'Organization',
    name: 'CEM Teknoloji',
  },
  knowsAbout: [
    'Yazılım geliştirme',
    'Full stack developer',
    'ERP',
    'MRP',
    'B2B',
    '.NET',
    'React',
    'Node.js',
    'SQL',
  ],
  sameAs: [...site.social.map((s) => s.href), ...site.stores.map((s) => s.href)],
});

const graphFor = (path: string) => {
  const url = `${site.url}${path === '/' ? '' : path}`;
  const title = pageTitles[path] || `${site.name} — ${site.title}`;
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'WebSite',
      '@id': `${site.url}/#website`,
      name: `${site.name} | Yazılımcı`,
      alternateName: [site.asciiName, 'Mustafa Gürses yazılımcı'],
      url: site.url,
      inLanguage: 'tr-TR',
      publisher: { '@id': `${site.url}/#person` },
    },
    personJson(),
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: title,
      description: pageDescriptions[path] || pageDescriptions['/'],
      inLanguage: 'tr-TR',
      isPartOf: { '@id': `${site.url}/#website` },
      about: { '@id': `${site.url}/#person` },
      breadcrumb: { '@id': `${url}#breadcrumb` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Mustafa Gürses',
          item: site.url,
        },
        ...(path === '/'
          ? []
          : [
              {
                '@type': 'ListItem',
                position: 2,
                name: title,
                item: url,
              },
            ]),
      ],
    },
  ];

  if (path === '/about') {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: aboutFaqs.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    });
  }

  if (path === '/projects') {
    graph.push({
      '@type': 'ItemList',
      name: 'Mustafa Gürses yazılımcı projeleri',
      itemListElement: projects.map((project, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: project.title,
        url: project.href,
      })),
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
};

export const Seo: React.FC = () => {
  const { pathname } = useLocation();
  const path = pageTitles[pathname] ? pathname : '/';
  const is404 = !pageTitles[pathname] && pathname !== '/';

  useEffect(() => {
    const title = is404
      ? `Sayfa bulunamadı — ${site.name}`
      : pageTitles[path];
    const description = is404
      ? pageDescriptions['/']
      : pageDescriptions[path];
    const url = `${site.url}${pathname === '/' ? '' : pathname}`;
    const image = `${site.url}/brand/logo.png`;

    document.title = title;
    document.documentElement.lang = 'tr';

    setMeta('name', 'description', description);
    setMeta('name', 'author', site.name);
    setMeta(
      'name',
      'robots',
      is404 ? 'noindex, follow' : 'index, follow, max-image-preview:large'
    );
    setMeta('name', 'googlebot', is404 ? 'noindex, follow' : 'index, follow');
    setMeta(
      'name',
      'keywords',
      'Mustafa Gürses, Mustafa Gurses, Mustafa, yazılımcı, full stack developer, Gaziantep yazılımcı, ERP yazılımcı, CEM Teknoloji'
    );
    setMeta('property', 'og:type', pathname === '/about' ? 'profile' : 'website');
    setMeta('property', 'og:site_name', `${site.name} | Yazılımcı`);
    setMeta('property', 'og:locale', 'tr_TR');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:image:alt', `${site.name} yazılımcı logosu`);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);
    setLink('canonical', is404 ? site.url : url);

    let ld = document.getElementById('ld-json') as HTMLScriptElement | null;
    if (!ld) {
      ld = document.createElement('script');
      ld.id = 'ld-json';
      ld.type = 'application/ld+json';
      document.head.appendChild(ld);
    }
    ld.text = JSON.stringify(graphFor(is404 ? '/' : path));
  }, [pathname, path, is404]);

  return null;
};
